import { useRef, useState } from 'react'
import { X } from 'lucide-react'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { Illustration_ } from 'src/api/db/apiProductImages'
import { useGetDecorationInfo } from 'src/api/server/useGetDecorationInfo'
import { KlayderRecommendsSection } from 'src/shared/components/ProductShared/KlayderRecommendsSection'
import { ProductDescription } from 'src/shared/components/ProductShared/ProductDescription'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { ColorVariantSelector } from './components/ColorVariantSelector'
import { ProductImageCarousel } from './components/ProductImageCarousel'
import { TabButton } from './components/TabButton'

type DecorationOption = {
  print_code?: string
  info?: { name?: string; one_liner?: string }
}

/** Print techniques the supplier offers for this product. The server's fallback
 *  ("no_print", used when it has no print data) is not a technique. */
const realDecorations = (data: unknown): DecorationOption[] =>
  Array.isArray(data)
    ? (data as DecorationOption[]).filter(
        (d) => d?.print_code && d.print_code !== 'no_print' && d.info?.name
      )
    : []

/**
 * Product details on a proposal: a centred modal with the images beside the
 * details on desktop and above them on mobile. Product information only — no
 * prices, shop actions or links away from the proposal. The Decorations tab
 * lists the print techniques by name only: catalogue print prices are
 * estimates, and the proposal already carries the quoted decoration price.
 *
 * The colour picked here is a local preview. It never touches the URL (a URL
 * change made the page behind scroll) and never changes the proposal's option.
 */
export const ProposalProductModal = ({
  open,
  setOpen,
  product_id,
  chosen_product_variant_color,
  product_variant_colors,
  product_name,
  product_description,
  klayder_recommends,
  klayder_recommendation_text,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  product_id: number
  /** The colour on the proposal line. */
  chosen_product_variant_color: ProductVariantColor_
  product_variant_colors: ProductVariantColor_[]
  product_name: string
  product_description: string
  klayder_recommends: boolean
  klayder_recommendation_text: string
}) => {
  const { t } = useLocalization()
  const swatchesRef = useRef<HTMLDivElement>(null)
  const [previewColor, setPreviewColor] = useState(chosen_product_variant_color)
  const [selectedIllustration, setSelectedIllustration] =
    useState<Illustration_ | null>(null)
  const [section, setSection] = useState<'overview' | 'decorations'>('overview')
  const { decorations: decorationData } = useGetDecorationInfo({ product_id })
  const decorations = realDecorations(decorationData)

  const proposalColorName = chosen_product_variant_color?.color || ''
  const previewColorName = previewColor?.color || ''
  const isPreviewingOtherColor =
    previewColor?.id !== chosen_product_variant_color?.id
  const hasOtherColors = (product_variant_colors?.length ?? 0) > 1

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        showClose={false}
        overlayClassName="bg-black/25 backdrop-blur-none"
        className="proposal-page flex max-h-[min(90dvh,720px)] w-[calc(100vw-1.5rem)] max-w-[880px] flex-col gap-0 overflow-hidden bg-white p-0 font-tiktok-sans font-extralight text-proposal-ink"
      >
        <DialogTitle className="sr-only">{product_name}</DialogTitle>

        <div className="min-h-0 flex-1 overflow-y-auto md:grid md:grid-cols-2 md:overflow-hidden">
          <div className="p-3 sm:p-4 md:flex md:flex-col md:justify-center md:bg-proposal-ground/60 md:p-6">
            <ProductImageCarousel
              chosen_product_variant_color_={previewColor}
              selectedIllustration={selectedIllustration}
              setSelectedIllustration={setSelectedIllustration}
              product_id={product_id}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-5 px-4 pb-6 pt-1 sm:px-5 md:overflow-y-auto md:p-8 md:pr-14">
            <h2 className="m-0 text-2xl leading-tight">{product_name}</h2>

            {previewColor ? (
              <div className="flex flex-col gap-2">
                <span className="text-sm text-proposal-ink-soft">
                  {t('Colour', 'Färg')}:{' '}
                  <span className="font-semibold text-proposal-ink">
                    {previewColorName}
                  </span>
                </span>
                {hasOtherColors ? (
                  <ColorVariantSelector
                    scrollContainerRef={swatchesRef}
                    chosen_product_variant_color_={previewColor}
                    product_variant_colors={product_variant_colors}
                    setProduct_variant_color={setPreviewColor}
                  />
                ) : null}
                {hasOtherColors ? (
                  <p className="m-0 text-[13px] leading-normal text-proposal-ink-muted">
                    {isPreviewingOtherColor ? (
                      <>
                        {t(
                          `Preview only. Your proposal stays in ${proposalColorName}.`,
                          `Endast förhandsvisning. Ert förslag är kvar i ${proposalColorName}.`
                        )}{' '}
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewColor(chosen_product_variant_color)
                          }
                          className="rounded-sm font-semibold text-proposal-orange-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-proposal-orange"
                        >
                          {t('Show proposal colour', 'Visa förslagets färg')}
                        </button>
                      </>
                    ) : (
                      t(
                        'This is the colour in your proposal. Other colours are for preview only and do not change it.',
                        'Det här är färgen i ert förslag. Andra färger är bara en förhandsvisning och ändrar det inte.'
                      )
                    )}
                  </p>
                ) : null}
              </div>
            ) : null}

            {decorations.length > 0 ? (
              <div className="flex gap-6 border-b border-[var(--raised-border)]">
                <TabButton
                  selected={section === 'overview'}
                  onClick={() => setSection('overview')}
                >
                  {t('Overview', 'Översikt')}
                </TabButton>
                <TabButton
                  selected={section === 'decorations'}
                  onClick={() => setSection('decorations')}
                >
                  {t('Decorations', 'Dekorationer')} ({decorations.length})
                </TabButton>
              </div>
            ) : null}

            {section === 'decorations' && decorations.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {decorations.map((decoration) => (
                  <li
                    key={decoration.print_code}
                    className="flex flex-col gap-1 rounded-[14px] border border-[var(--raised-border)] bg-proposal-ground/60 p-4"
                  >
                    <span className="text-[15px] font-semibold text-proposal-ink">
                      {t(decoration.info?.name ?? '')}
                    </span>
                    {decoration.info?.one_liner ? (
                      <span className="text-[13px] leading-normal text-proposal-ink-soft">
                        {t(decoration.info.one_liner)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <KlayderRecommendsSection
                  klayder_recommends={klayder_recommends}
                  klayder_recommendation_text={klayder_recommendation_text}
                />

                {product_description ? (
                  <ProductDescription description={product_description} />
                ) : null}
              </>
            )}
          </div>
        </div>

        <DialogClose className="absolute right-3 top-3 z-[1] flex size-10 items-center justify-center rounded-full bg-white/80 opacity-90 shadow-sm transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
          <X className="h-5 w-5" />
          <span className="sr-only">{t('Close', 'Stäng')}</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}
